import { addSkill, getSkillsMap } from '@/api/skills';
import { useAuth } from '@/providers/AuthProvider';
import { Skill } from '@/types';
import { handleAPIError } from '@/utils/errorHandler';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const useSkills = () => {
  const { organization } = useAuth();
  const orgId = organization?.id;

  const query = useQuery({
    queryKey: ['skills', organization?.id],
    queryFn: getSkillsMap,
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};

export const useCreateSkill = () => {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id;

  const mutation = useMutation({
    mutationFn: (skill: Skill) => addSkill(skill),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['skills', orgId],
      });
    },
    onError: (error) => {
      handleAPIError(error);
    },
  });

  return {
    createSkill: mutation.mutate,
    createSkillAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
